import {
  DownloadHandoutLetters,
  LetterAcknowledgment,
  LetterHandouts,
  OverdueElections,
  SendLetter,
  type HandoutView,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { request, requestDownload } from './client';
import { ApiError } from './errors';

export type { HandoutView };
interface PageOptions {
  page?: number;
  size?: number;
}
/** The view of the letters to hand out an address asks for; copies have their own card. */
export function handoutView(value: string | null): Exclude<HandoutView, 'copies'> {
  return value === 'pending' || value === 'handed' ? value : 'all';
}
function pageParams(base: Record<string, string>, { page = 1, size }: PageOptions) {
  const params = new URLSearchParams(base);
  params.set('page', String(page));
  if (size !== undefined) params.set('size', String(size));
  return params;
}
export function handoutsQuery({
  view = 'all',
  page = 1,
  size,
}: PageOptions & { view?: HandoutView } = {}) {
  const params = pageParams({ view }, { page, ...(size !== undefined && { size }) });
  return queryOptions({
    queryKey: ['letter-handouts', 'letters', { view, page, size }] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/letter-handouts?${params}`, { schema: LetterHandouts, signal }),
    staleTime: 5_000,
  });
}
export function overdueElectionsQuery({ page = 1, size }: PageOptions = {}) {
  const params = pageParams({}, { page, ...(size !== undefined && { size }) });
  return queryOptions({
    queryKey: ['letter-handouts', 'overdue', { page, size }] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/letter-handouts/overdue?${params}`, { schema: OverdueElections, signal }),
    staleTime: 5_000,
  });
}
export async function acknowledgeLetter(documentId: string): Promise<LetterAcknowledgment> {
  const saved = await request(
    `/api/v1/letter-handouts/${encodeURIComponent(documentId)}/acknowledgment`,
    {
      method: 'POST',
      body: {},
      schema: LetterAcknowledgment,
    },
  );
  if (saved.documentId !== documentId)
    throw new ApiError(200, 'invalid_response', 'The letter acknowledgment did not match.');
  return saved;
}

/**
 * APP-245 D9: send the letter to its employee, which counts as handed out, with an optional note.
 * The receipt must be this letter's, recorded as sent.
 */
export async function sendLetter(documentId: string, note: string): Promise<LetterAcknowledgment> {
  const trimmed = note.trim();
  const saved = await request(`/api/v1/letter-handouts/${encodeURIComponent(documentId)}/send`, {
    method: 'POST',
    body: SendLetter.parse(trimmed === '' ? {} : { note: trimmed }),
    schema: LetterAcknowledgment,
  });
  if (saved.documentId !== documentId || saved.method !== 'sent')
    throw new ApiError(200, 'invalid_response', 'The letter receipt did not match.');
  return saved;
}

function isChosen(
  letters: readonly string[] | { every: HandoutView },
): letters is readonly string[] {
  return Array.isArray(letters);
}

/**
 * A zip of the chosen letters (up to 25), or (APP-245 D15) `{ every: view }`: every cleared letter
 * in that view, on every page.
 */
export function downloadHandoutLetters(
  letters: readonly string[] | { every: HandoutView },
  signal?: AbortSignal,
): Promise<Blob> {
  // Never `'every' in letters`: an array has `every` too.
  const body = DownloadHandoutLetters.parse(
    isChosen(letters) ? { documentIds: letters } : { every: letters.every },
  );
  return requestDownload('/api/v1/letter-handouts/download', {
    method: 'POST',
    body,
    contentType: 'application/zip',
    maxBytes: 50 * 1024 * 1024,
    ...(signal !== undefined && { signal }),
  });
}
